import React, { useEffect, useRef, useState } from "react";
import WaveSurfer from "wavesurfer.js";
import { Play, Pause, Headphones } from "lucide-react";
import { assetUrl } from "../lib/api";

// Construit l'URL d'embed pour les providers tiers (SoundCloud, Spotify, Audiomack, YouTube audio)
const buildProviderEmbed = (source, rawUrl) => {
  if (!rawUrl) return null;
  if (source === "soundcloud") {
    const enc = encodeURIComponent(rawUrl);
    return `https://w.soundcloud.com/player/?url=${enc}&color=%23facc15&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false&visual=false`;
  }
  if (source === "spotify") {
    // open.spotify.com/track/<id> → embed/track/<id>
    const m = rawUrl.match(/spotify\.com\/(track|episode|album|playlist)\/([a-zA-Z0-9]+)/);
    if (m) return `https://open.spotify.com/embed/${m[1]}/${m[2]}?utm_source=arnak`;
    return rawUrl;
  }
  if (source === "audiomack") {
    // Audiomack supporte iframe direct via /embed sur leur URL
    return rawUrl.replace("audiomack.com/", "audiomack.com/embed-player/");
  }
  if (source === "youtube") {
    // Extrait video_id depuis URL YouTube classique
    const m = rawUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/);
    if (m) return `https://www.youtube.com/embed/${m[1]}?rel=0&modestbranding=1`;
    return rawUrl;
  }
  return rawUrl;
};

// Lecteur WaveSurfer pour les fichiers audio uploadés ou MP3 directs
const FilePlayer = ({ src, color = "#facc15" }) => {
  const containerRef = useRef(null);
  const wsRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;
    const ws = WaveSurfer.create({
      container: containerRef.current,
      waveColor: "rgba(255,255,255,0.25)",
      progressColor: color,
      cursorColor: "transparent",
      barWidth: 2,
      barGap: 2,
      barRadius: 2,
      height: 56,
      normalize: true,
      backend: "WebAudio",
    });
    wsRef.current = ws;
    ws.load(src);
    ws.on("ready", () => { setReady(true); setDuration(ws.getDuration()); });
    ws.on("audioprocess", () => setCurrent(ws.getCurrentTime()));
    ws.on("seeking", () => setCurrent(ws.getCurrentTime()));
    ws.on("play", () => setPlaying(true));
    ws.on("pause", () => setPlaying(false));
    ws.on("finish", () => { setPlaying(false); setCurrent(0); });
    return () => { try { ws.destroy(); } catch (e) { /* ignore */ } };
  }, [src, color]);

  const toggle = () => {
    const ws = wsRef.current;
    if (!ws || !ready) return;
    ws.playPause();
  };

  const fmt = (t) => {
    if (!Number.isFinite(t)) return "0:00";
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  return (
    <div className="flex items-center gap-3 w-full" data-testid="audio-file-player">
      <button
        type="button"
        onClick={toggle}
        disabled={!ready}
        className="w-12 h-12 shrink-0 rounded-full bg-yellow-400 hover:bg-yellow-300 disabled:opacity-40 text-black flex items-center justify-center transition shadow-lg shadow-yellow-400/20"
        aria-label={playing ? "Pause" : "Lecture"}
        data-testid="audio-play-btn"
      >
        {playing ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
      </button>
      <div className="flex-1 min-w-0">
        <div ref={containerRef} className="w-full" />
        <div className="flex justify-between text-[10px] text-white/45 font-mono tabular-nums mt-1.5">
          <span>{fmt(current)}</span>
          <span>{fmt(duration)}</span>
        </div>
      </div>
    </div>
  );
};

// Composant principal — choisit le bon player selon la source
export const ArnakAudioPlayer = ({ item, fallbackThumb }) => {
  const src = item.audio_source;
  const thumb = item.thumbnail_url || fallbackThumb;
  const thumbResolved = thumb ? assetUrl(thumb) : null;

  // Provider externe : iframe officielle (SoundCloud/Spotify/Audiomack/YouTube)
  if (["soundcloud", "spotify", "audiomack", "youtube"].includes(src)) {
    const embed = buildProviderEmbed(src, item.embed_url);
    const heights = { soundcloud: 166, spotify: 152, audiomack: 240, youtube: 380 };
    const h = heights[src] || 166;
    return (
      <article className="rounded-2xl overflow-hidden border border-white/10 bg-white/[0.02]" data-testid="audio-card">
        <header className="px-4 pt-4 pb-3 flex items-center gap-3">
          {thumbResolved ? (
            <img src={thumbResolved} alt={item.title} className="w-12 h-12 rounded object-cover shrink-0" />
          ) : (
            <div className="w-12 h-12 rounded bg-yellow-400/15 flex items-center justify-center shrink-0">
              <Headphones size={20} className="text-yellow-300" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest font-bold text-yellow-400/80 mb-0.5">
              {src === "soundcloud" ? "SoundCloud" : src === "spotify" ? "Spotify" : src === "audiomack" ? "Audiomack" : "YouTube"}
            </p>
            <h3 className="font-black tracking-tight truncate" style={{ fontFamily: "'Anton', sans-serif" }}>{item.title}</h3>
          </div>
        </header>
        <iframe
          src={embed}
          title={item.title}
          width="100%"
          height={h}
          frameBorder="0"
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          loading="lazy"
          className="block"
        />
      </article>
    );
  }

  // Fichier uploadé ou URL directe → wavesurfer
  const fileSrc = src === "upload" ? assetUrl(item.audio_file_url) : item.embed_url;
  if (!fileSrc) return null;

  return (
    <article className="rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.01] p-4 md:p-5" data-testid="audio-card">
      <div className="flex items-start gap-4">
        {thumbResolved ? (
          <img src={thumbResolved} alt={item.title} className="w-16 h-16 md:w-20 md:h-20 rounded-lg object-cover shrink-0 shadow-lg" />
        ) : (
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-lg bg-yellow-400/15 flex items-center justify-center shrink-0 border border-yellow-400/30">
            <Headphones size={26} className="text-yellow-300" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest font-bold text-yellow-400/80 mb-1">
            {src === "upload" ? "ARNAK Original" : "Audio direct"}
          </p>
          <h3 className="font-black text-lg tracking-tight truncate" style={{ fontFamily: "'Anton', sans-serif" }}>
            {item.title}
          </h3>
        </div>
      </div>
      <div className="mt-3">
        <FilePlayer src={fileSrc} />
      </div>
    </article>
  );
};

export default ArnakAudioPlayer;
